import { formatMinor, type SupplierToTeach } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Input } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { bookkeepingReferenceQuery } from '../../shared/api/bookkeeping-queries.js';
import {
  reviewKeys,
  suppliersToTeachQuery,
  teachSupplier,
} from '../../shared/api/review-queries.js';

/**
 * Teach a supplier once.
 *
 * After automatic filing, what still waits is mostly the same question, asked
 * receipt by receipt: who is this supplier, and what are their purchases for?
 * Grouped here, it is asked once per supplier. The answer is remembered, every
 * spelling included and every store of a chain, and each receipt it settles
 * files itself.
 */
export function SuppliersToTeach({ organizationId }: { organizationId: string }) {
  const groups = useQuery(suppliersToTeachQuery(organizationId));
  const reference = useQuery(bookkeepingReferenceQuery(organizationId));
  const [message, setMessage] = useState<string | null>(null);

  const rows = groups.data ?? [];
  if (rows.length === 0) return null;

  const waiting = rows.reduce((sum, row) => sum + row.documentCount, 0);

  return (
    <Card>
      <CardHeader
        title={`Suppliers to teach (${String(rows.length)})`}
        description={`${String(waiting)} receipts are waiting only because nobody has said who these suppliers are. Answer once per supplier and their receipts file themselves.`}
      />
      <CardBody className="flex flex-col gap-2">
        {message === null ? null : <Alert tone="success">{message}</Alert>}
        {rows.map((row) => (
          <TeachRow
            key={row.groupKey}
            organizationId={organizationId}
            row={row}
            vendors={reference.data?.vendors ?? []}
            categories={(reference.data?.categories ?? []).filter(
              (category) =>
                category.accountType === 'expense' || category.accountType === 'cost_of_goods_sold',
            )}
            onTaught={setMessage}
          />
        ))}
      </CardBody>
    </Card>
  );
}

function TeachRow({
  organizationId,
  row,
  vendors,
  categories,
  onTaught,
}: {
  organizationId: string;
  row: SupplierToTeach;
  vendors: readonly { id: string; displayName: string; defaultCategoryId: string | null }[];
  categories: readonly { id: string; code: string | null; name: string }[];
  onTaught: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [vendorId, setVendorId] = useState<string>(row.suggestedVendorId ?? 'new');
  const [newName, setNewName] = useState(titleCase(row.groupKey));
  const [categoryId, setCategoryId] = useState<string>(
    row.suggestedCategoryId ??
      vendors.find((vendor) => vendor.id === row.suggestedVendorId)?.defaultCategoryId ??
      '',
  );
  const [error, setError] = useState<string | null>(null);

  const teach = useMutation({
    mutationFn: () =>
      teachSupplier(organizationId, {
        groupKey: row.groupKey,
        names: row.names,
        categoryId,
        ...(vendorId === 'new' ? { newVendorName: newName } : { vendorId }),
      }),
    onSuccess: (result) => {
      setError(null);
      onTaught(
        result.filed === 0
          ? `Remembered. None of its receipts could file yet; they were read less surely and wait for a look.`
          : `Remembered. ${String(result.filed)} receipt${result.filed === 1 ? '' : 's'} filed themselves.`,
      );
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all(organizationId) });
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'That was not saved.'),
  });

  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-border px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className="font-medium text-content">{row.names.join(' · ')}</span>
        <span className="text-content-muted">
          {row.documentCount} receipt{row.documentCount === 1 ? '' : 's'},{' '}
          {formatMinor(row.totalMinor)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Which supplier is this?"
          className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 text-sm"
          value={vendorId}
          onChange={(event) => {
            setVendorId(event.target.value);
            const chosen = vendors.find((vendor) => vendor.id === event.target.value);
            if (chosen?.defaultCategoryId !== null && chosen?.defaultCategoryId !== undefined) {
              setCategoryId(chosen.defaultCategoryId);
            }
          }}
        >
          <option value="new">A new supplier…</option>
          {vendors.map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.displayName}
            </option>
          ))}
        </select>
        {vendorId === 'new' ? (
          <Input
            aria-label="New supplier's name"
            className="w-48"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
        ) : null}
        <select
          aria-label="Their purchases go to"
          className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 text-sm"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          <option value="">Their purchases go to…</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.code === null ? '' : `${category.code} `}
              {category.name}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          onClick={() => teach.mutate()}
          isLoading={teach.isPending}
          disabled={categoryId === '' || (vendorId === 'new' && newName.trim() === '')}
        >
          Remember
        </Button>
      </div>
      {error === null ? null : <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
